import { patchUser } from "@/api/privateRequests/user/patchUser";
import { currentPathUnderLocale } from "@/config/localePath";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { trackChangeLanguage } from "@/events/navBarMixpanel";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/lib/utils";
import {
  CONTENT_LANGUAGE_NATIVE_NAMES,
  type ContentLanguage,
  SUPPORTED_CONTENT_LANGUAGES,
  contentLanguagePrimarySubtag,
  getContentLanguage,
  setContentLanguage,
} from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { ChevronDown, Globe } from "lucide-react";
import { useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

/*
  Desktop panel look, flip to compare. "nav" matches the nav bar's own More
  dropdown; "design-system" borrows the Dropdown panel and row classes.
*/
const DESKTOP_PANEL: "nav" | "design-system" = "design-system";

const DESKTOP_PANEL_CLASSES = {
  nav: {
    panel: "rounded bg-panel-background shadow-button",
    row: "px-4 py-2 font-medium whitespace-nowrap hover:bg-panel-foreground hover:text-white",
    selected: "text-white",
    idle: "text-dark-tertiary",
  },
  "design-system": {
    panel: "min-w-40 gap-1 rounded-[20px] bg-dropdown-background p-2 shadow-lg",
    row: "h-8 rounded-[20px] px-3 whitespace-nowrap transition-colors",
    selected: "bg-blue-900/50 text-neutral-50",
    idle: "text-neutral-100 hover:bg-neutral-700",
  },
}[DESKTOP_PANEL];

/*
  Every list and detail page is fetched in the current language, and that
  state lives in redux, so the switch is a full page load rather than per-slice
  invalidation (DEV-3572). It lands on the same page under the new locale
  prefix, since the prefix in the URL wins at boot (DEV-3681). The profile
  mirror is awaited so the write lands before the load tears the page down; a
  failure is logged, never blocking.
*/
export const changeContentLanguage = async (
  language: ContentLanguage,
  userIsLoggedIn: boolean,
) => {
  trackChangeLanguage({
    language,
    previous_language: getContentLanguage(),
  });
  if (language === getContentLanguage()) return;
  setContentLanguage(language);
  if (userIsLoggedIn) {
    try {
      await patchUser({ language });
    } catch (error) {
      console.error("Failed to store content language on profile", error);
    }
  }
  window.location.assign(currentPathUnderLocale(language));
};

/**
 * Desktop navigation bar. The trigger is a globe and the two-letter code
 * ("EN"), muted like the nav links, so it reads as a setting rather than a
 * destination; the open list spells the languages out in full.
 */
export const ContentLanguageDropdown = ({
  userIsLoggedIn,
}: {
  userIsLoggedIn: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false));
  useEscape(() => setOpen(false));
  const current = getContentLanguage();

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label={t("language.current", {
          name: CONTENT_LANGUAGE_NATIVE_NAMES[current],
        })}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "text-xs font-medium uppercase px-2 py-1.5 rounded flex items-center gap-x-1 select-none hover:text-white",
          open ? "text-white" : "text-dark-tertiary",
        )}
        onClick={() => setOpen(!open)}
      >
        <Globe className="size-4" strokeWidth={1.75} />
        {/* Below large-desktop icon only; first to go when the bar overflows (see NavBarMega). */}
        <span className="hidden large-desktop:block" data-squeeze="1">
          {contentLanguagePrimarySubtag(current)}
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute right-0 top-full z-50 mt-1 flex flex-col",
            DESKTOP_PANEL_CLASSES.panel,
          )}
        >
          {SUPPORTED_CONTENT_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              role="option"
              aria-selected={language === current}
              className={cn(
                "flex items-center text-left text-sm",
                DESKTOP_PANEL_CLASSES.row,
                language === current
                  ? DESKTOP_PANEL_CLASSES.selected
                  : DESKTOP_PANEL_CLASSES.idle,
              )}
              onClick={() => {
                setOpen(false);
                changeContentLanguage(language, userIsLoggedIn);
              }}
            >
              {CONTENT_LANGUAGE_NATIVE_NAMES[language]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Touch-device drawer: a row styled like "More" (globe, current language,
 * chevron) that expands in place into one row per language.
 */
export const ContentLanguageDrawerItem = ({
  userIsLoggedIn,
}: {
  userIsLoggedIn: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = getContentLanguage();

  return (
    <div>
      <button
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-x-2.5 w-full h-11 px-4 text-sm font-medium text-foreground-muted"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <Globe className="size-5" strokeWidth={1.5} />
        <span>{CONTENT_LANGUAGE_NATIVE_NAMES[current]}</span>
        <ChevronDown
          className={cn(
            "ml-auto size-4 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>
      <Collapsible open={open}>
        <div
          role="listbox"
          aria-label={t("language.label")}
          className="flex flex-col"
        >
          {SUPPORTED_CONTENT_LANGUAGES.map((language) => (
            <button
              key={language}
              role="option"
              aria-selected={language === current}
              className={cn(
                "flex items-center w-full h-10 pl-11.5 pr-4 text-left text-sm",
                language === current
                  ? "text-foreground"
                  : "text-foreground-muted",
              )}
              onClick={(e) => {
                e.stopPropagation();
                changeContentLanguage(language, userIsLoggedIn);
              }}
            >
              {CONTENT_LANGUAGE_NATIVE_NAMES[language]}
            </button>
          ))}
        </div>
      </Collapsible>
    </div>
  );
};
